/**
 * Convert a TipTap / ProseMirror document into markdown source.
 *
 * The editor is a WYSIWYG rich-text surface; we only need to reduce it to
 * markdown when shipping the test to the lambda backend at playback time
 * (`global_test_task`) or when exporting/importing.
 *
 * This implementation covers the StarterKit nodes we actually use:
 * heading, paragraph, bulletList, orderedList, listItem, codeBlock,
 * blockquote, horizontalRule, hardBreak, plus inline marks bold/italic/
 * strike/code/link, and the custom `mention` node which we render as
 * plain text.
 */

type ProseNode = {
  type: string;
  attrs?: Record<string, any>;
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, any> }>;
  content?: ProseNode[];
};

const escapeText = (text: string): string =>
  text
    .replace(/\\/g, "\\\\")
    .replace(/([*_`~])/g, "\\$1")
    .replace(/\n/g, "  \n");

const wrapMarks = (text: string, marks?: ProseNode["marks"]): string => {
  if (text.length === 0 || marks == null || marks.length === 0) return text;
  let out = text;
  for (const mark of marks) {
    switch (mark.type) {
      case "bold":
        out = `**${out}**`;
        break;
      case "italic":
        out = `*${out}*`;
        break;
      case "strike":
        out = `~~${out}~~`;
        break;
      case "code":
        out = `\`${out}\``;
        break;
      case "link": {
        const href = mark.attrs?.href ?? "";
        out = `[${out}](${href})`;
        break;
      }
      default:
        break;
    }
  }
  return out;
};

const inlineToMarkdown = (nodes: ProseNode[] | undefined): string => {
  if (!nodes) return "";
  return nodes
    .map((node) => {
      switch (node.type) {
        case "text":
          return wrapMarks(escapeText(node.text ?? ""), node.marks);
        case "hardBreak":
          return "  \n";
        case "mention": {
          const label = node.attrs?.label ?? "";
          return `@${label}`;
        }
        default:
          return inlineToMarkdown(node.content);
      }
    })
    .join("");
};

const listToMarkdown = (
  node: ProseNode,
  ordered: boolean,
  depth: number
): string => {
  const start = (node.attrs?.start as number) ?? 1;
  const indent = "  ".repeat(depth);
  return (node.content ?? [])
    .map((listItem, index) => {
      const bullet = ordered ? `${start + index}.` : "-";
      const itemLines = (listItem.content ?? [])
        .map((child, idx) => {
          if (child.type === "bulletList" || child.type === "orderedList") {
            return listToMarkdown(child, child.type === "orderedList", depth + 1);
          }
          // First paragraph sits on the bullet line; subsequent block content
          // is indented under it.
          const rendered = blockToMarkdown(child, depth + 1);
          if (idx === 0) return rendered;
          return rendered
            .split("\n")
            .map((line) => `${indent}  ${line}`)
            .join("\n");
        })
        .filter((s) => s.length > 0)
        .join("\n");
      return `${indent}${bullet} ${itemLines}`;
    })
    .join("\n");
};

const blockToMarkdown = (node: ProseNode, depth = 0): string => {
  switch (node.type) {
    case "heading": {
      const level = Math.min(Math.max(node.attrs?.level ?? 1, 1), 6);
      return `${"#".repeat(level)} ${inlineToMarkdown(node.content)}`;
    }
    case "paragraph":
      return inlineToMarkdown(node.content);
    case "bulletList":
      return listToMarkdown(node, false, depth);
    case "orderedList":
      return listToMarkdown(node, true, depth);
    case "codeBlock": {
      const lang = node.attrs?.language ?? "";
      const body = (node.content ?? [])
        .map((c) => c.text ?? "")
        .join("");
      return `\`\`\`${lang}\n${body}\n\`\`\``;
    }
    case "blockquote":
      return (node.content ?? [])
        .map((c) => blockToMarkdown(c, depth))
        .join("\n\n")
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
    case "horizontalRule":
      return "---";
    case "hardBreak":
      return "  ";
    default:
      // Fallback: try inline content, otherwise recurse.
      if (node.content) {
        return node.content
          .map((c) => blockToMarkdown(c, depth))
          .join("\n\n");
      }
      return inlineToMarkdown([node]);
  }
};

export const tiptapJsonToMarkdown = (doc: ProseNode | null | undefined): string => {
  if (doc == null) return "";
  if (doc.type === "doc") {
    return (doc.content ?? [])
      .map((c) => blockToMarkdown(c, 0))
      .filter((s) => s.length > 0)
      .join("\n\n")
      .trim();
  }
  return blockToMarkdown(doc, 0);
};

/**
 * True if the document contains at least one heading whose text starts with
 * "goal" and at least one whose text starts with "step(s)" (any heading
 * level, case-insensitive). Used as a preflight before running so we can
 * offer to optimise loose user input into the canonical format.
 */
export const docHasRequiredMarkdownHeadings = (
  doc: ProseNode | null | undefined
): boolean => {
  if (doc == null || doc.type !== "doc") return false;
  const headingTexts: string[] = [];
  for (const node of doc.content ?? []) {
    if (node?.type !== "heading") continue;
    const text = (node.content ?? [])
      .map((c) => (typeof c?.text === "string" ? c.text : ""))
      .join("")
      .trim()
      .toLowerCase();
    if (text.length > 0) headingTexts.push(text);
  }
  const hasGoal = headingTexts.some((t) => /^goal\b/.test(t));
  const hasSteps = headingTexts.some((t) => /^steps?\b/.test(t));
  return hasGoal && hasSteps;
};

/**
 * Convert a small subset of markdown (the subset our editor produces and the
 * import agent emits) into HTML suitable for `editor.commands.setContent`.
 * TipTap parses the HTML into ProseMirror nodes via its registered
 * extensions. We deliberately keep this minimal — round-tripping
 * heading/list/paragraph/inline emphasis is enough for the import flow.
 */
const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const renderInline = (text: string): string => {
  let out = escapeHtml(text);
  // Code spans first so other patterns don't munge them.
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, "$1<em>$2</em>");
  out = out.replace(/~~([^~]+)~~/g, "<s>$1</s>");
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2">$1</a>'
  );
  return out;
};

export const markdownToHtml = (markdown: string): string => {
  const lines = (markdown ?? "").replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let paragraphBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    out.push(`<p>${renderInline(paragraphBuffer.join(" "))}</p>`);
    paragraphBuffer = [];
  };
  const closeList = () => {
    if (listType != null) {
      out.push(listType === "ul" ? "</ul>" : "</ol>");
      listType = null;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/g, "");
    if (line.length === 0) {
      flushParagraph();
      closeList();
      continue;
    }
    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line);
    if (headingMatch) {
      flushParagraph();
      closeList();
      const level = headingMatch[1].length;
      out.push(`<h${level}>${renderInline(headingMatch[2])}</h${level}>`);
      continue;
    }
    const orderedMatch = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (orderedMatch) {
      flushParagraph();
      if (listType !== "ol") {
        closeList();
        out.push("<ol>");
        listType = "ol";
      }
      out.push(`<li><p>${renderInline(orderedMatch[1])}</p></li>`);
      continue;
    }
    const bulletMatch = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bulletMatch) {
      flushParagraph();
      if (listType !== "ul") {
        closeList();
        out.push("<ul>");
        listType = "ul";
      }
      out.push(`<li><p>${renderInline(bulletMatch[1])}</p></li>`);
      continue;
    }
    closeList();
    paragraphBuffer.push(line);
  }
  flushParagraph();
  closeList();
  return out.join("\n");
};
